/*
Usage:
<skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
<script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
May want to include above module globally if element is used in cart drawer
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/core/lit-core.min.js'

const skioCartStyles = css`
  .skio-cart-upsell {
    width: 100%;
    margin-top: 10px;
    padding: 5px;
    color: #154331;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.33;
    text-align: center;
    letter-spacing: 0;
    background: #fff;
    border: solid 2px #b8dac2;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s linear;
  }

  .skio-cart-upgrade {
    width: 100%;
    display: inline-block;
    margin-top: 10px;
    padding: 5px;
    color: #154331;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.33;
    text-align: center;
    letter-spacing: 0;
    background: #fff;
    border: solid 2px #b8dac2;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s linear;
  }

  .skio-cart-upgrade:hover {
    color: white;
    border-color: #b8dac2;
    background: #b8dac2;
  }

  .volume-discount-display {
    margin-top: 5px;
    text-align: center;
    font-size: 13px;
  }
`

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String },

    rules: { type: Array },
    rule: { type: Object },
    nextRule: { type: Object },
    quantity: { type: Number },
    cart: { type: Object }
  }

  constructor() {
    super()

    this.item = null
    this.line = null

    this.product = null
    this.selectedVariant = null

    this.availableSellingPlanGroups = null

    this.selectedSellingPlanGroup = null
    this.selectedSellingPlan = null

    this.discountFormat = 'percent'

    this.currency = window.Shopify.currency.active
    this.moneyFormatter = new Intl.NumberFormat(window.Shopify.locale, {
      style: 'currency',
      currency: this.currency
    })

    this.rules = []
    this.rule = null
    this.nextRule = null
    this.cart = null
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')

    this.rules = await this.fetchRules()
    await this.getRule()
    await this.getNextRule()
  }

  upgradeButton = () => {
    if (this.availableSellingPlanGroups?.length > 0) {
      return html`
        <!-- <button class="skio-cart-upgrade" type="button" @click=${() =>
          this.selectSellingPlan(this.availableSellingPlanGroups[0].selected_selling_plan.id)}>Upgrade to Autoship & Save ${this.discount(
            this.availableSellingPlanGroups[0].selected_selling_plan
          ).percent}</button> -->
        <button
          class="skio-cart-upgrade"
          type="button"
          @click=${() => this.selectSellingPlan(this.availableSellingPlanGroups[0].selected_selling_plan.id)}
        >
          Upgrade to Autoship & Save up to 25%
        </button>
      `
    } else {
      return html``
    }
  }

  volumeDiscountDisplay = () => {
    return html`
      <div class="volume-discount-display">
        ${this.nextRule
        ? html`
              <small>
                Add
                ${(this.nextRule?.minQuantityToDiscount || this.rules[0].minQuantityToDiscount) -
          this.eligibleItemCount(this.nextRule || this.rules[0])}
                more item(s) to unlock ${this.getRuleDiscount(this.nextRule)} off
              </small>
            `
        : ''}
      </div>
    `
  }

  changeFrequency = () => {
    return html`
      <select class="skio-cart-upsell" skio-cart-upsell="${this.key}" @change=${(e) => this.selectSellingPlan(e.target.value)}>
        ${!this.product.requires_selling_plan
        ? html`
              <optgroup label="One Time Purchase">
                <option value="">One-time</option>
              </optgroup>
            `
        : ''}
        ${this.availableSellingPlanGroups
        ? this.availableSellingPlanGroups.map(
          (group, index) =>
            html`
                  <optgroup label="${group.name} (Save ${this.discount(group.selected_selling_plan).percent})">
                    ${group
                ? this.getAvailableSellingPlans(group).map(
                  (selling_plan) =>
                    html`
                              <option value="${selling_plan.id}" .selected=${selling_plan.id == this.selectedSellingPlan?.id}>
                                ${selling_plan.name}
                              </option>
                            `
                )
                : ''}
                  </optgroup>
                `
        )
        : ''}
      </select>

      ${this.volumeDiscountDisplay()}
    `
  }

  render() {
    if (!this.item || !this.product || !this.selectedVariant) return
    return this.selectedSellingPlan ? this.changeFrequency() : this.upgradeButton()
  }

  updated = (changed) => {
    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle)
    }

    if (changed.has('product') && this.product) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) =>
          selling_plan_group.app_id === 'SKIO' &&
          !selling_plan_group.name.toLowerCase().includes('hidden-') &&
          !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      this.selectedVariant = this.product.variants.find((variant) => variant.id == this.item.variant_id)

      if (this.item.selling_plan_allocation) {
        this.selectedSellingPlanGroup = filteredSellingPlanGroups.find((group) =>
          group.selling_plans.find((selling_plan) => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id)
        )
        this.selectedSellingPlan = this.selectedSellingPlanGroup?.selling_plans.find(
          (selling_plan) => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id
        )
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) =>
          selling_plan_group.app_id === 'SKIO' &&
          !selling_plan_group.name.toLowerCase().includes('hidden-') &&
          !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on filteredSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some((selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups.length) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selected_selling_plan = group.selling_plans[0]
        })
      }
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem()
    }

    if (changed.has('cart')) {
      this.getRule()
      this.getNextRule()
    }
  }

  log = (...args) => {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;')
  }

  error = (...args) => {
    args.unshift('%c [skio cart upsell]', 'color: #ff0000')
    console.error.apply(console, args)
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then((response) => response.json())
        .then((response) => {
          return response.rules.filter((rule) => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    if (!this.cart?.items) return 0

    let cartItems = Number(
      this.cart.items.filter((item) => item.vendor !== "SavedBy").reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some((gid) => gid.includes(item.id) && item.selling_plan_allocation) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    this.log('eligibleItemCount', {
      cart: this.cart,
      rule: rule,
      rules: this.rules,
      cartItems
    })

    return cartItems
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1
    }

    this.rules.forEach((rule, index) => {
      this.log('rule loop', rule)
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null;
  }

  getNextRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1
    }

    const filteredRules = this.rules.filter((rule) => rule.minQuantityToDiscount)
    for (const [index, rule] of filteredRules.entries()) {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount > this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
          break
        }
      }
    }

    this.nextRule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  getRuleDiscount(rule) {
    if (!rule) return

    const { discountAmount, discountType } = rule

    if (discountType === 'PERCENTAGE') {
      return `${discountAmount}%`
    } else if (discountType === 'FIXED_VALUE') {
      return this.moneyFormatter.format(discountAmount)
    }

    return '0%'
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find((group) => group.selling_plans.some((plan) => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      const intervals = intervalDateMatches[0].match(/\d+/g)
      multiplier = intervals[0] / (intervals[1] || 1)
      multiplier = multiplier > 1 ? multiplier : 1
    }

    const price = this.selectedVariant.price
    const compareAtPrice =
      this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    this.log('test', price_adjustment)

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    this.log('discount tes 2t', discount)

    return discount
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter((selling_plan) =>
      this.selectedVariant.selling_plan_allocations.some((selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart_form',
        section: document.getElementById('cart_form').dataset.id,
        selector: '.cart__item-list'
      }
    ]
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  updateLineItem() {
    if (!this.key || !this.item) return
    if (this.item.selling_plan_allocation?.selling_plan?.id == this.selectedSellingPlan?.id) return

    let data

    if (this.selectedSellingPlan) {
      data = JSON.stringify({
        id: this.key,
        quantity: this.item.quantity,
        selling_plan: this.selectedSellingPlan ? this.selectedSellingPlan?.id : null
      })
    } else {
      data = JSON.stringify({
        id: this.key,
        quantity: this.item.quantity,
        selling_plan: null
      })
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: data
    })
      .then((response) => response.text())
      .then((cart) => {
        // if (Shopify.theme.jsCart !== 'undefined' && window.location.href.includes('/cart')) {
        //   Shopify.theme.jsCart.updateView(JSON.parse(cart), this.key);
        // }

        // if (typeof Shopify.theme.jsAjaxCart !== 'undefined') {
        //   Shopify.theme.jsAjaxCart.updateView();
        // }

        // if (window.location.href.includes('/cart')) {
        //   const parsedState = JSON.parse(cart);
        //   this.getSectionsToRender().forEach((section => {
        //     const elementToReplace =
        //       document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        //     elementToReplace.innerHTML =
        //       this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        //   }));
        // }

        if (goCart) {
          goCart.fetchCart()
        }
      })
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(id) {
    if (!id) {
      this.selectedSellingPlanGroup = null
      this.selectedSellingPlan = null
      return
    }

    let group = this.availableSellingPlanGroups.find((group) => group.selling_plans.find((selling_plan) => selling_plan.id == id))
    let selling_plan = group.selling_plans.find((x) => x.id == id)

    if (selling_plan) {
      group.selected_selling_plan = selling_plan
      this.selectedSellingPlanGroup = group
      this.selectedSellingPlan = selling_plan
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
        element.value +
        ' for variant ' +
        this.selectedVariant.id +
        ' from product ' +
        this.product.id +
        ' : ' +
        this.product.handle
      )
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  fetchProduct = async (handle) => {
    let productCache = window.sessionStorage.skioCartProductCache ? JSON.parse(window.sessionStorage.skioCartProductCache) : []
    let cachedProduct = productCache ? productCache.find((product) => product.handle == handle) : null

    if (cachedProduct) {
      this.product = cachedProduct
    } else {
      await fetch(`/products/${handle}.js`)
        .then((response) => response.json())
        .then((response) => {
          this.product = response
          productCache.push(response)
          window.sessionStorage.skioCartProductCache = JSON.stringify(productCache)
          this.requestUpdate()
        })
    }
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell)
